import { shouldWaitForPingRequest } from './pingRequest';
import { UrlFallback } from './urlFallback';

function joinBaseAndPath(base: string, path: string): string {
  if (!path) {
    return base;
  }
  return `${base}/${path.replace(/^\/+/, '')}`;
}

/** Структурный тип вместо импорта конкретного Angular-сервиса — см. waitUntilReadyWithPreloader. */
interface PreloaderCounter {
  increment(): void;
  decrement(): void;
}

/**
 * Суффиксный "вид" на общий пул UrlFallback для конкретного потребителя (REST, SignalR-хаб
 * и т.п.). Несколько UrlFallbackView с разными суффиксами могут делить один и тот же
 * UrlFallback — тогда у пары proxyHosts+pingRequestAddress остаётся один цикл pingRequest
 * и одно состояние round-robin/ping в localStorage, а не по одному на каждого потребителя.
 */
export class UrlFallbackView {
  constructor(
    private readonly source: UrlFallback,
    private readonly suffix: string,
  ) {}

  /**
   * Исходный UrlFallback пула. Нужен, только если потребителю действительно требуется
   * работать с пулом напрямую — остальным достаточно методов самого UrlFallbackView.
   */
  getSource(): UrlFallback {
    return this.source;
  }

  getUrls(): readonly string[] {
    return this.source
      .getUrls()
      .map((host) => joinBaseAndPath(host, this.suffix));
  }

  tryGetCurrentAvailableUrl(
    excludedIndexes: ReadonlySet<number> = new Set<number>(),
  ): { url: string; index: number } | null {
    const target = this.source.tryGetCurrentAvailableUrl(excludedIndexes);
    return target
      ? { url: joinBaseAndPath(target.url, this.suffix), index: target.index }
      : null;
  }

  /** Alias для совместимости с Crm/App. */
  tryGetNearestAvailableUrl(
    excludedIndexes: ReadonlySet<number> = new Set<number>(),
  ): { url: string; index: number } | null {
    return this.tryGetCurrentAvailableUrl(excludedIndexes);
  }

  shouldFallback(httpStatus: number): boolean {
    return this.source.shouldFallback(httpStatus);
  }

  checkAndRecordResult(httpStatus: number, plannedIndex: number): boolean {
    return this.source.checkAndRecordResult(httpStatus, plannedIndex);
  }

  /** storageKey пула (напр. "api"), для вычисления ключа ping-флагов. */
  getStorageKeySuffix(): string {
    return this.source.getStorageKeySuffix();
  }

  /** Есть ли пригодный результат ping у пула (делегируется общему UrlFallback). */
  hasUsablePingSnapshot(): boolean {
    return this.source.hasUsablePingSnapshot();
  }

  /** Момент последнего успешного ответа хоста в ping пула (делегируется общему UrlFallback). */
  getLastPingSuccessAt(): number {
    return this.source.getLastPingSuccessAt();
  }

  /** Пришёл ли свежий пригодный результат ping с момента stamp (делегируется UrlFallback). */
  hasFreshUsablePingSince(stamp: number): boolean {
    return this.source.hasFreshUsablePingSince(stamp);
  }

  /**
   * Текущий лучший URL пула + path, без ping-гейтинга и без retry — для навигационных ссылок
   * и iframe-подобных сценариев (src модалки оплаты и т.п.), где сам браузер выполняет запрос
   * и JS не видит ни ответа, ни ошибки. checkAndRecordResult в таком сценарии вызвать неоткуда,
   * поэтому "fallback" здесь — это то, что успел решить последний завершённый фоновый
   * pingRequest: между двумя его циклами (см. controlRequestInterval) ссылка ещё может
   * указывать на упавший хост.
   */
  resolveUrl(path: string = ''): string {
    const base = this.tryGetCurrentAvailableUrl()?.url ?? this.getUrls()[0];
    return path ? joinBaseAndPath(base, path) : base;
  }

  /**
   * Ждёт, пока по пулу имеет смысл ждать публикацию свежего ping (см.
   * shouldWaitForPingRequest): нет пригодного snapshot, ping идёт и не истёк grace. Как только
   * появился snapshot либо вышел grace — выходим, не дожидаясь снятия lock (см. resolveUrl).
   */
  async waitUntilReady(pollMs: number = 250): Promise<void> {
    while (shouldWaitForPingRequest(this)) {
      await new Promise<void>((resolve) => setTimeout(resolve, pollMs));
    }
  }

  /**
   * То же, что waitUntilReady(), но включает preloader (счётчик busy) только если ожидание
   * реально нужно — чтобы не мигать плашкой на каждый вызов, когда ждать нечего.
   */
  async waitUntilReadyWithPreloader(
    preloader: PreloaderCounter,
    pollMs: number = 250,
  ): Promise<void> {
    if (!shouldWaitForPingRequest(this)) {
      return;
    }
    preloader.increment();
    try {
      await this.waitUntilReady(pollMs);
    } finally {
      preloader.decrement();
    }
  }
}
